import { useState, useEffect, useRef, useCallback, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Star, Volume2, VolumeX, Calendar, ChevronUp, ChevronDown, Search, X, Zap, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEGAL_DISCLAIMER = "Este servicio es de acompañamiento y escucha. No es un servicio médico y no sustituye atención profesional.";

const DEFAULT_PHOTO = "https://images.unsplash.com/photo-1629121699638-976764fb4c8c?w=400&h=600&fit=crop";

const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

// Global mute state
const useGlobalMute = () => {
  const [isMuted, setIsMuted] = useState(() => {
    const stored = localStorage.getItem('videoMuted');
    return stored === null ? true : stored === 'true';
  });

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newValue = !prev;
      localStorage.setItem('videoMuted', String(newValue));
      return newValue;
    });
  }, []);

  return { isMuted, toggleMute };
};

// Optimized Video Card with lazy loading
const FullScreenVideoCard = memo(({ listener, isMuted, onToggleMute, isActive, isPreload }) => {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  // Determine if we should load the video source
  // Load if: active, or is the next video to preload
  const shouldLoadVideo = isActive || isPreload;

  // Handle play/pause based on active state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive && shouldLoadVideo) {
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive, shouldLoadVideo]);

  // Sync mute state
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
  };

  const handleVideoError = () => {
    setVideoError(true);
  };

  const thumbnailUrl = getFullUrl(listener.photo_url) || DEFAULT_PHOTO;
  const videoUrl = getFullUrl(listener.video_url);

  return (
    <div 
      className="relative w-full h-full bg-black flex-shrink-0 snap-start snap-always flex items-center justify-center"
      data-testid={`listener-card-${listener.username}`}
    >
      {/* Thumbnail placeholder - always visible until video loads */}
      {listener.video_url && !videoLoaded && !videoError && (
        <div className="absolute inset-0 flex items-center justify-center z-[5]">
          <img
            src={thumbnailUrl}
            alt={listener.name}
            className="max-w-full max-h-full w-auto h-auto object-contain"
            loading="lazy"
          />
          {/* Loading indicator */}
          {shouldLoadVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Loader2 className="w-12 h-12 text-white animate-spin" />
            </div>
          )}
        </div>
      )}

      {/* Video - only load source when active or preloading */}
      {listener.video_url && !videoError ? (
        <video
          ref={videoRef}
          src={shouldLoadVideo ? videoUrl : undefined}
          className={`max-w-full max-h-full w-auto h-auto object-contain transition-opacity duration-300 ${
            videoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loop
          muted={isMuted}
          playsInline
          preload={isActive ? "auto" : isPreload ? "metadata" : "none"}
          poster={thumbnailUrl}
          onLoadedData={handleVideoLoaded}
          onError={handleVideoError}
        />
      ) : (
        <img
          src={thumbnailUrl}
          alt={listener.name}
          className="max-w-full max-h-full w-auto h-auto object-contain"
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = DEFAULT_PHOTO;
          }}
        />
      )}

      {/* Mute/Unmute button */}
      {listener.video_url && !videoError && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleMute();
          }}
          className="absolute top-4 right-4 w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white z-20 hover:bg-black/70 transition-colors"
          aria-label={isMuted ? "Activar sonido" : "Silenciar"}
        >
          {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
        </button>
      )}

      {/* Online Badge */}
      {listener.is_online && listener.availability_mode === "instant" && (
        <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-2 shadow-lg z-20">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          Disponible ahora
        </div>
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none" />

      {/* Content overlay */}
      <div className="absolute inset-x-0 bottom-0 p-6 pb-8 z-10">
        {/* Name and Rating */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className="text-3xl font-serif font-bold text-white drop-shadow-lg">
            {listener.name}
          </h2>
          {listener.average_rating && (
            <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
              <Star className="w-4 h-4 text-amber-400 fill-current" />
              <span className="text-sm font-semibold text-white">{listener.average_rating}</span>
            </div>
          )}
        </div>

        {/* Bio */}
        {listener.bio && (
          <p className="text-white/90 text-base leading-relaxed mb-4 line-clamp-3">
            {listener.bio}
          </p>
        )}

        {/* Price and CTA */}
        <div className="flex items-center justify-between gap-4">
          <span className="text-white">
            <span className="text-3xl font-bold">${listener.price_per_session}</span>
            <span className="text-white/70 text-base ml-2">USD</span>
          </span>
          <Link to={`/l/${listener.username}`} className="flex-shrink-0">
            <Button 
              className="bg-white text-stone-900 hover:bg-white/90 font-bold px-6 py-6 text-lg rounded-full shadow-lg"
              data-testid={`book-${listener.username}`}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Agendar
            </Button>
          </Link>
        </div>

        {/* Legal disclaimer */}
        <p className="mt-4 text-white/50 text-xs text-center">
          {LEGAL_DISCLAIMER}
        </p>
      </div>
    </div>
  );
});

FullScreenVideoCard.displayName = 'FullScreenVideoCard';

export default function Discovery() {
  const [listeners, setListeners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAvailableNow, setFilterAvailableNow] = useState(false);
  const { isMuted, toggleMute } = useGlobalMute();
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchListeners();
  }, [filterAvailableNow]);

  const fetchListeners = async () => {
    try {
      const params = filterAvailableNow ? "?available_now=true" : "";
      const response = await axios.get(`${API}/listeners${params}`);
      setListeners(response.data);
      setCurrentIndex(0);
    } catch (error) {
      console.error("Error fetching listeners:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredListeners = listeners.filter(listener =>
    listener.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (listener.bio && listener.bio.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle scroll snap
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const itemHeight = container.clientHeight;
    const newIndex = Math.round(scrollTop / itemHeight);
    
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < filteredListeners.length) {
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, filteredListeners.length]);

  // Navigate to specific index
  const goToIndex = useCallback((index) => {
    if (!containerRef.current) return;
    if (index < 0 || index >= filteredListeners.length) return;
    
    const container = containerRef.current;
    const itemHeight = container.clientHeight;
    container.scrollTo({
      top: index * itemHeight,
      behavior: 'smooth'
    });
    setCurrentIndex(index);
  }, [filteredListeners.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'j') {
        goToIndex(currentIndex + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        goToIndex(currentIndex - 1);
      } else if (e.key === 'm') {
        toggleMute();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, goToIndex, toggleMute]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="spinner border-white"></div>
      </div>
    );
  }

  if (filteredListeners.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center text-white p-6">
        <p className="text-xl mb-4 text-center">
          {searchTerm 
            ? "No se encontraron listeners."
            : filterAvailableNow
            ? "No hay listeners disponibles ahora."
            : "Aún no hay listeners disponibles."}
        </p>
        <div className="flex gap-3">
          {searchTerm && (
            <Button 
              onClick={() => setSearchTerm("")}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              Limpiar búsqueda
            </Button>
          )}
          {filterAvailableNow && (
            <Button 
              onClick={() => setFilterAvailableNow(false)}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              Ver todos
            </Button>
          )}
        </div>
        <Button 
          onClick={() => navigate('/')}
          className="mt-4 bg-white text-black hover:bg-white/90"
        >
          Volver al inicio
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black">
      {/* Search Overlay */}
      {showSearch && (
        <div className="absolute inset-x-0 top-0 z-30 bg-black/95 backdrop-blur-sm p-4 animate-in slide-in-from-top">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
              <input
                type="text"
                placeholder="Buscar listener..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-full pl-10 pr-4 py-3 text-white placeholder:text-white/50 focus:outline-none focus:border-white/40"
                autoFocus
                data-testid="search-input"
              />
            </div>
            <button
              onClick={() => setShowSearch(false)}
              className="w-10 h-10 flex items-center justify-center text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Filter button */}
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => setFilterAvailableNow(!filterAvailableNow)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filterAvailableNow 
                  ? 'bg-green-500 text-white' 
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
              data-testid="filter-available-now"
            >
              <Zap className="w-4 h-4" />
              Disponible ahora
            </button>
          </div>
        </div>
      )}

      {/* Search Toggle Button */}
      {!showSearch && (
        <button
          onClick={() => setShowSearch(true)}
          className="absolute top-4 left-4 z-20 w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          aria-label="Buscar"
        >
          <Search className="w-6 h-6" />
        </button>
      )}

      {/* Navigation Arrows - Desktop */}
      <div className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 flex-col gap-2">
        <button
          onClick={() => goToIndex(currentIndex - 1)}
          disabled={currentIndex === 0}
          className="w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Anterior"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
        <button
          onClick={() => goToIndex(currentIndex + 1)}
          disabled={currentIndex === filteredListeners.length - 1}
          className="w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Siguiente"
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>

      {/* Progress Dots */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-1.5 md:mr-16">
        {filteredListeners.map((_, index) => (
          <button
            key={index}
            onClick={() => goToIndex(index)}
            className={`w-1.5 h-1.5 rounded-full transition-all ${
              index === currentIndex 
                ? 'bg-white h-4' 
                : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Ir al listener ${index + 1}`}
          />
        ))}
      </div>

      {/* Counter */}
      <div className="absolute bottom-4 left-4 z-20 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
        <span className="text-white text-sm font-medium">
          {currentIndex + 1} / {filteredListeners.length}
        </span>
      </div>

      {/* Scroll Container */}
      <div
        ref={containerRef}
        className="h-full w-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
        onScroll={handleScroll}
        style={{ scrollSnapType: 'y mandatory' }}
      >
        {filteredListeners.map((listener, index) => (
          <div 
            key={listener.id} 
            className="h-full w-full"
            style={{ scrollSnapAlign: 'start' }}
          >
            <FullScreenVideoCard
              listener={listener}
              isMuted={isMuted}
              onToggleMute={toggleMute}
              isActive={index === currentIndex}
              isPreload={index === currentIndex + 1}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
